/* eslint-disable react/prop-types */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Layers, Utensils } from "lucide-react";

export function MixAndMatchDetailsDialog({ isOpen, onClose, item }) {
  if (!item) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto bg-cblack2 text-cwhite border border-cblack6">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-cyellow/10 text-cyellow">
              <Layers size={20} />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-cwhite">
                {item?.name} Details
              </DialogTitle>
              <p className="text-xs text-cwhite3 mt-0.5">
                Overview of categories and choices for this Mix & Match
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 bg-cblack3 rounded-lg border border-cblack6">
              <span className="text-xs text-cwhite3 block">Max Choices</span>
              <span className="text-base font-bold text-cyellow">
                {item?.max_choose ?? "N/A"}
              </span>
            </div>
            <div className="p-3 bg-cblack3 rounded-lg border border-cblack6">
              <span className="text-xs text-cwhite3 block">VAT</span>
              <span className="text-base font-bold text-cwhite">
                {item?.vat ?? 0}%
              </span>
            </div>
            <div className="p-3 bg-cblack3 rounded-lg border border-cblack6">
              <span className="text-xs text-cwhite3 block">Categories</span>
              <span className="text-base font-bold text-cwhite">
                {item?.categories?.length || item?.categories_count || 0}
              </span>
            </div>
            <div className="p-3 bg-cblack3 rounded-lg border border-cblack6">
              <span className="text-xs text-cwhite3 block">Status</span>
              <span
                className={`text-sm font-semibold capitalize ${
                  item?.status === "active"
                    ? "text-cgreen"
                    : item?.status === "inactive"
                    ? "text-cred"
                    : "text-amber-500"
                }`}
              >
                {item?.status || "active"}
              </span>
            </div>
          </div>

          {/* Categories & Items List */}
          <div>
            <h4 className="text-sm font-medium text-cwhite mb-2 flex items-center gap-1.5">
              <Utensils size={15} className="text-cyellow" /> Categories & Items
            </h4>

            {item?.categories && item.categories.length > 0 ? (
              <div className="space-y-3">
                {item.categories.map((cat, idx) => (
                  <div
                    key={cat.id || idx}
                    className="p-3.5 bg-cblack3 rounded-lg border border-cblack6"
                  >
                    <div className="flex justify-between items-center mb-2.5 pb-2 border-b border-cblack6">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-cblack7 text-cwhite2">
                          #{idx + 1}
                        </span>
                        <span className="font-semibold text-cwhite text-sm">
                          {cat.category_name}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-cyellow">
                        ${cat.price ?? "0.00"}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      {cat?.items && cat.items.length > 0 ? (
                        cat.items.map((it, itIdx) => {
                          const itemName =
                            typeof it === "string" ? it : it?.name;
                          return (
                            <Badge
                              key={it?.id || itIdx}
                              variant="secondary"
                              className="bg-cblack7 text-cwhite2 border border-cblack6 px-2.5 py-1 text-xs"
                            >
                              {itemName}
                            </Badge>
                          );
                        })
                      ) : (
                        <span className="text-xs text-cwhite3 italic">
                          No items listed in this category
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-cwhite3 bg-cblack3 rounded-lg text-xs">
                No categories available.
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default MixAndMatchDetailsDialog;
